// ---------------------------------------------------------------------------
// useSubmitTemplate — owns the create + edit submit flows for the form.
//
// Bundles:
//   • submitting / submitError state (create flow)
//   • editSaving / editError state (edit flow)
//   • handleSubmit — PUT N agents in parallel, then PUT the template
//   • handleEditSave — PATCH the template (server PATCH replaces all list
//     fields when non-null; identity fields only included when changed)
//   • handleCancel — navigate back to the templates list
//
// The hook's input surface is wide (every form field that feeds the body),
// which is the cost of keeping all submit logic in one place rather than
// inlined in the form. Worth it: drift between create + edit was a real
// risk before `buildTemplateBody` consolidated the structural assembly.
// ---------------------------------------------------------------------------

import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { useHttpClient } from '../../../../api/HttpClientContext.js';
import { EvaluationTemplatesService } from '../../../../services/evaluationTemplatesService.js';
import { useUserBi } from '../../../../telemetry/hooks/useUserBi/useUserBi.js';
import {
  EntityType,
  OperationType,
  PanelType,
} from '../../../../telemetry/userbi.js';
import type { EvaluationTemplate } from '../../../../types/evaluationTemplate.js';
import type { BindingRow } from '../bindings.utils.js';
import { applyBindingsToParts } from '../bindings.utils.js';
import type {
  DataLakeFileSelector,
  DataSourceMode,
  EvaluationTemplateWithConfig,
  ParallelismChoice,
  TaskDraft,
  VariableRow,
} from '../EvaluationTemplateForm.types.js';
import { buildTemplateBody } from '../EvaluationTemplateForm.utils.js';
import { getFormValidity } from '../EvaluationTemplateForm.validation.js';

export interface UseSubmitTemplateParams {
  // Identity
  jobName: string;
  jobDisplayName: string;
  jobDescription: string;
  // Body-building state
  taskDrafts: TaskDraft[];
  variables: VariableRow[];
  /** Inferred declarations required by selected resources but absent from the form. */
  additionalVariables?: Record<string, unknown>;
  /** Per-variable runtime bindings, emitted as `with:` clauses on the tasks. */
  bindingRows: BindingRow[];
  /** Definite local binding/path errors block saving the template. */
  bindingFlowHasErrors?: boolean;
  querySetMode: DataSourceMode;
  querySetItems: string[] | null;
  dataSetEnabled: boolean;
  dataSetMode: DataSourceMode;
  dataSetItems: string[] | null;
  dataSetExistingId: string | null;
  dataSetExistingVersion: number | null;
  dataSetFileSelector: DataLakeFileSelector[];
  workersText: string;
  parallelism: ParallelismChoice;
  // Environment
  workspaceLoading: boolean;
  activeWorkspaceName: string | null | undefined;
  // Edit-mode inputs
  initialTemplate: EvaluationTemplateWithConfig | undefined;
  onCreated?: (created: EvaluationTemplate) => void;
  onEditSaved: ((updated: EvaluationTemplate) => void) | undefined;
}

export interface UseSubmitTemplateResult {
  canSubmit: boolean;
  submitting: boolean;
  submitError: string | null;
  editSaving: boolean;
  editError: string | null;
  handleSubmit: () => Promise<void>;
  handleEditSave: () => Promise<void>;
  handleCancel: () => void;
}

function buildBoundTemplateParts(params: UseSubmitTemplateParams) {
  const parts = buildTemplateBody({
    taskDrafts: params.taskDrafts,
    variables: params.variables,
    querySetItems: params.querySetItems,
    dataSetEnabled: params.dataSetEnabled,
    dataSetMode: params.dataSetMode,
    dataSetItems: params.dataSetItems,
    dataSetExistingId: params.dataSetExistingId,
    dataSetExistingVersion: params.dataSetExistingVersion,
    dataSetFileSelector: params.dataSetFileSelector,
    workersText: params.workersText,
    parallelism: params.parallelism,
  });
  return applyBindingsToParts(
    {
      ...parts,
      variables: {
        ...params.additionalVariables,
        ...parts.variables,
      },
    },
    params.bindingRows,
  );
}

function evaluationTemplatesPath(workspaceName: string): string {
  return `/workspaces/${encodeURIComponent(workspaceName)}/evaluation-templates`;
}

export function useSubmitTemplate(
  params: UseSubmitTemplateParams,
): UseSubmitTemplateResult {
  const navigate = useNavigate();
  const httpClient = useHttpClient();
  const { trackOperation } = useUserBi();

  const templatesService = useMemo(
    () => new EvaluationTemplatesService(httpClient),
    [httpClient],
  );

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Edit submit state — kept separate so create + edit can coexist without
  // leaking each other's loading/error flags.
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Compute validity here (rather than at the form) so the form doesn't
  // have to thread `submitting` back into the validator — submitting lives
  // in this hook, and the validator needs it for the canSubmit gate.
  const { canSubmit } = getFormValidity({
    jobName: params.jobName,
    taskDrafts: params.taskDrafts,
    variables: params.variables,
    querySetMode: params.querySetMode,
    querySetItems: params.querySetItems,
    dataSetEnabled: params.dataSetEnabled,
    dataSetMode: params.dataSetMode,
    dataSetExistingId: params.dataSetExistingId,
    dataSetFileSelector: params.dataSetFileSelector,
    dataSetItems: params.dataSetItems,
    bindingFlowHasErrors: params.bindingFlowHasErrors,
    submitting,
    workspaceLoading: params.workspaceLoading,
    activeWorkspaceName: params.activeWorkspaceName,
  });

  const handleCancel = useCallback(() => {
    const { activeWorkspaceName } = params;
    if (activeWorkspaceName === null || activeWorkspaceName === undefined) {
      return;
    }
    void navigate(evaluationTemplatesPath(activeWorkspaceName));
  }, [navigate, params]);

  const handleEditSave = useCallback(async () => {
    const {
      initialTemplate,
      activeWorkspaceName,
      jobDisplayName,
      jobDescription,
    } = params;
    if (initialTemplate === undefined) return;
    if (activeWorkspaceName === null || activeWorkspaceName === undefined) {
      return;
    }
    if (!canSubmit || editSaving) return;

    // Server PATCH (PatchEvaluationTemplateRequest.cs) full-replaces list
    // fields when non-null, so we send the entire current state of
    // querySets/dataSets/tasks/variables/concurrency. Identity fields only
    // go in the body if the user actually changed them — keeps the audit
    // log meaningful and avoids no-op re-saves.
    const originalDisplayName = initialTemplate.displayName ?? '';
    const originalDescription = initialTemplate.description ?? '';
    const trimmedDisplayName = jobDisplayName.trim();
    const trimmedDescription = jobDescription.trim();

    const body: Record<string, unknown> = {
      ...buildBoundTemplateParts(params),
    };
    if (trimmedDisplayName !== originalDisplayName) {
      body.displayName = trimmedDisplayName;
    }
    if (trimmedDescription !== originalDescription) {
      body.description = trimmedDescription;
    }

    setEditSaving(true);
    setEditError(null);
    try {
      const updated = await trackOperation(
        {
          panelType: PanelType.EvaluationTemplatesPage,
          entityType: EntityType.EvaluationTemplate,
          operation: OperationType.Update,
        },
        () =>
          templatesService.patch(
            activeWorkspaceName,
            initialTemplate.name,
            body,
          ),
      );
      params.onEditSaved?.(updated);
    } catch (err) {
      setEditError(
        err instanceof Error ? err.message : 'Failed to save changes.',
      );
    } finally {
      setEditSaving(false);
    }
  }, [params, canSubmit, editSaving, templatesService, trackOperation]);

  const handleSubmit = useCallback(async () => {
    const { activeWorkspaceName, jobName, jobDisplayName, jobDescription } =
      params;
    if (!canSubmit) return;
    if (activeWorkspaceName === null || activeWorkspaceName === undefined) {
      return;
    }
    setSubmitting(true);
    setSubmitError(null);

    try {
      // PUT the template (it references existing agents + graders by name).
      // Drop the variables map if empty so the request stays minimal —
      // server treats `{}` as "clear all", which is the wrong semantic for
      // create symmetry with PATCH.
      const parts = buildBoundTemplateParts(params);
      const templateBody = {
        displayName:
          jobDisplayName.trim().length > 0 ? jobDisplayName.trim() : undefined,
        description:
          jobDescription.trim().length > 0 ? jobDescription.trim() : undefined,
        ...parts,
        ...(Object.keys(parts.variables).length === 0
          ? { variables: undefined }
          : {}),
      } as unknown as Parameters<typeof templatesService.create>[2];

      const created = await trackOperation(
        {
          panelType: PanelType.EvaluationTemplatesPage,
          entityType: EntityType.EvaluationTemplate,
          operation: OperationType.Create,
        },
        () =>
          templatesService.create(activeWorkspaceName, jobName, templateBody),
      );
      params.onCreated?.(created);
      void navigate(
        `${evaluationTemplatesPath(activeWorkspaceName)}/${encodeURIComponent(created.name)}`,
      );
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : 'Failed to save template.',
      );
    } finally {
      setSubmitting(false);
    }
  }, [params, canSubmit, templatesService, navigate, trackOperation]);

  return {
    canSubmit,
    submitting,
    submitError,
    editSaving,
    editError,
    handleSubmit,
    handleEditSave,
    handleCancel,
  };
}
